<template>
  <div class="project-installation">
    <AccessBlock :id="PROJECT_ROUGH_PLANNING_BLOCK">
      <div class="accordion" role="tablist">
        <div class="mb-3">
          <b-button
            block
            v-b-toggle.project-installation-rough-planning
            variant="outline-primary"
            class="text-left accordion-header"
          >
            <i class="fa fa-calendar mr-2"></i>{{ $t('roughPlanning') }}
            <i class="fa fa-chevron-down float-right mt-1"></i>
          </b-button>
          <b-collapse id="project-installation-rough-planning" role="tabpanel">
            <div class="accordion-content px-2 py-2">
              <ProjectRoughPlanning
                v-if="roughPlanningProject"
                :blockId="PROJECT_ROUGH_PLANNING_BLOCK"
                :project="roughPlanningProject"
              />
            </div>
          </b-collapse>
        </div>
      </div>
    </AccessBlock>

    <AccessBlock :id="PROJECT_APPOINTMENT_NOTES_BLOCK">
      <div class="accordion" role="tablist">
        <div class="mb-3">
          <b-button
            block
            v-b-toggle.project-installation-appointment-notes
            variant="outline-primary"
            class="text-left accordion-header"
          >
            <i class="fa fa-sticky-note-o mr-2"></i>{{ $t('appointmentNotes') }}
            <i class="fa fa-chevron-down float-right mt-1"></i>
          </b-button>
          <b-collapse id="project-installation-appointment-notes" role="tabpanel" visible>
            <div class="accordion-content px-2 py-2">
              <ProjectAppointmentNotes
                v-if="appointmentNotesProject"
                :blockId="PROJECT_APPOINTMENT_NOTES_BLOCK"
                :project="appointmentNotesProject"
              />
            </div>
          </b-collapse>
        </div>
      </div>
    </AccessBlock>

    <AccessBlock :id="PROJECT_INSTALLATION_APPOINTMENTS_BLOCK">
      <div class="accordion" role="tablist">
        <div class="mb-3">
          <b-button
            block
            v-b-toggle.project-installation-appointments
            variant="outline-primary"
            class="text-left accordion-header"
          >
            <i class="fa fa-calendar-check-o mr-2"></i>{{ $t('installationAppointments') }}
            <i class="fa fa-chevron-down float-right mt-1"></i>
          </b-button>
          <b-collapse id="project-installation-appointments" role="tabpanel" visible>
            <div class="accordion-content px-2 py-2">
              <ProjectInstallationAppointments ref="installationAppointments" :project="project" />
            </div>
          </b-collapse>
        </div>
      </div>
    </AccessBlock>

    <AccessBlock :id="PROJECT_APPOINTMENT_SLOTS_BLOCK">
      <div v-if="showAppointmentCapacitySlots" class="accordion" role="tablist">
        <div class="mb-3">
          <b-button
            block
            v-b-toggle.project-installation-appointment-capacities
            variant="outline-primary"
            class="text-left accordion-header"
          >
            <i class="fa fa-calendar-plus-o mr-2"></i>{{ $t('appointmentCapacities') }}
            <i class="fa fa-chevron-down float-right mt-1"></i>
          </b-button>
          <b-collapse id="project-installation-appointment-capacities" role="tabpanel">
            <div class="accordion-content px-2 py-2">
              <ProjectAppointmentSlots :project="project" @booked="onAppointmentCapacityBooked" />
            </div>
          </b-collapse>
        </div>
      </div>
    </AccessBlock>

    <AccessBlock :id="PROJECT_TIME_TRACKING_BLOCK">
      <div class="accordion" role="tablist">
        <div class="mb-3">
          <b-button
            block
            v-b-toggle.project-installation-time-tracking
            variant="outline-primary"
            class="text-left accordion-header"
          >
            <i class="fa fa-clock-o mr-2"></i>{{ $t('time_tracking') }}
            <i class="fa fa-chevron-down float-right mt-1"></i>
          </b-button>
          <b-collapse id="project-installation-time-tracking" role="tabpanel">
            <div class="accordion-content px-2 py-2">
              <ProjectTimeTracking :project="project" />
            </div>
          </b-collapse>
        </div>
      </div>
    </AccessBlock>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import AccessBlock from '@/components/Access/AccessBlock.vue';
import ProjectRoughPlanning from '@/blocks/project/ProjectRoughPlanning';
import ProjectAppointmentNotes from '@/blocks/project/ProjectAppointmentNotes';
import ProjectInstallationAppointments from '@/blocks/project/ProjectInstallationAppointments';
import ProjectAppointmentSlots from '@/blocks/project/ProjectAppointmentSlots';
import ProjectTimeTracking from '@/blocks/project/ProjectTimeTracking';
import { AuthorizableActions, ResourceType } from '@/helpers/enums';
import { hasResourcePermission } from '@/helpers/userCapabilities';
import {
  PROJECT_APPOINTMENT_NOTES_BLOCK,
  PROJECT_APPOINTMENT_SLOTS_BLOCK,
  PROJECT_INSTALLATION_APPOINTMENTS_BLOCK,
  PROJECT_ROUGH_PLANNING_BLOCK,
  PROJECT_TIME_TRACKING_BLOCK,
} from '@/navigation/accessNodes';

/** Number of the unsaved project (ProjectEdit `_new`); it has no blocks yet. */
const NEW_PROJECT_NUMBER = '_new';

export default {
  components: {
    AccessBlock,
    ProjectRoughPlanning,
    ProjectAppointmentNotes,
    ProjectInstallationAppointments,
    ProjectAppointmentSlots,
    ProjectTimeTracking,
  },
  props: {
    project: { type: Object },
  },
  data() {
    return {
      PROJECT_ROUGH_PLANNING_BLOCK,
      PROJECT_APPOINTMENT_NOTES_BLOCK,
      PROJECT_INSTALLATION_APPOINTMENTS_BLOCK,
      PROJECT_APPOINTMENT_SLOTS_BLOCK,
      PROJECT_TIME_TRACKING_BLOCK,
    };
  },
  computed: {
    ...mapGetters('auth', ['isGuest', 'getCurrentUser', 'isOrganizationRoleNavigationEnabled']),
    /** Block data of project-rough-planning; the unsaved project goes to the block itself. */
    roughPlanningProject() {
      if (this.project.number === NEW_PROJECT_NUMBER) {
        return this.project;
      }

      return this.project.blocks?.roughPlanning ?? null;
    },
    /** Block data of project-appointment-notes; the unsaved project goes to the block itself. */
    appointmentNotesProject() {
      if (this.project.number === NEW_PROJECT_NUMBER) {
        return this.project;
      }

      return this.project.blocks?.appointmentNotes ?? null;
    },
    /**
     * Inside the block project-appointment-slots: Matrix-Authz the block level decides alone;
     * Legacy-Authz guests and ABAC, as before (see feature page projekt-montage).
     */
    showAppointmentCapacitySlots() {
      if (this.isOrganizationRoleNavigationEnabled) {
        return true;
      }
      if (this.isGuest) {
        return true;
      }
      return hasResourcePermission(this.getCurrentUser, ResourceType.APPOINTMENT_CAPACITY, [
        AuthorizableActions.LIST,
      ]);
    },
  },
  methods: {
    /**
     * The booking created an appointment: the view reloads the block Installationstermine (absent
     * without a level on it); the blocks know nothing of each other.
     */
    onAppointmentCapacityBooked() {
      this.$refs.installationAppointments?.loadTableData();
    },
  },
};
</script>
